import apiSlice from 'renderer/shared/state/apiSlice';
import {
  GetConfigRequest,
  GetConfigResponse,
  GetConfigResponseSchema,
  FinalizePurchaseRequest,
  PurchaseRequest,
  PurchaseResponse,
  RequiresActionResponse,
} from '../schemas/purchase.schemas';
import {
  applyCheckoutAuth,
  applyCheckoutAuthFromError,
  hasCheckoutAuth,
  wantsCheckoutAuth,
} from '../utils/applyCheckoutAuth';
import {
  GetPriceRequest,
  GetPriceResponse,
  PricesSchema,
} from '../schemas/prices.schema';

export interface PaypalCheckoutRequest {
  user: any;
  billingAddress: any;
  cartId?: string;
  purchases: any[];
  returnUrl: string;
  cancelUrl: string;
}

export interface PaypalCheckoutResponse {
  type: 'checkout';
  approvals: {
    id: string;
    kind: 'order' | 'subscription';
    productId: string;
    approvalUrl: string | null;
  }[];
  orders: {
    id: string;
    kind: 'order';
    productId: string;
    status: string;
  }[];
}

export interface PaypalCaptureRequest {
  id: string;
  type: 'order' | 'subscription' | 'vault';
  cartId?: string;
  isFinal?: boolean;
  purchases?: {
    productId: string;
    numberOfCopies: number;
    platform?: string;
    hostApplication?: any;
    serial?: string;
    isFinal?: boolean;
  }[];
}

export interface PaypalCaptureResponse {
  transactions: never[];
  id: string;
  status: string;
  captureId?: string;
  planId?: string;
  startTime?: string;
  paypalCaptureHeld?: boolean;
  confirmationMessage?: string;
  transactionId?: string;
  to?: string;
  customerName?: string;
  productName?: string;
  purchaseDate?: string;
  contactEmail?: string;
  companyName?: string;
  serialNumbers?: string[];
  newCustomer?: boolean;
  user?: {
    firstName: string;
    lastName: string;
    email: string;
    role: 'user' | 'admin';
    isEmailVerified: boolean;
    _id: string;
  };
  tokens?: {
    access: { token: string; expires: string };
    refresh: { token: string; expires: string };
  };
}

const invalidateCheckoutTags = (
  dispatch: (action: unknown) => void,
  payload: unknown,
) => {
  if (!hasCheckoutAuth(payload as PurchaseResponse)) return;

  dispatch(
    apiSlice.util.invalidateTags(['PaymentMethods', 'User', 'Notification']),
  );
};

const checkoutAuthOnQueryStarted = async (
  requestBody: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => void;
    queryFulfilled: Promise<{ data: unknown }>;
  },
) => {
  const allowAuth = wantsCheckoutAuth(requestBody);

  try {
    const { data } = await queryFulfilled;
    if (!allowAuth) return;

    applyCheckoutAuth(dispatch as never, data as PurchaseResponse);
    invalidateCheckoutTags(dispatch, data);
  } catch (error) {
    if (!allowAuth) return;

    applyCheckoutAuthFromError(dispatch as never, error);
    const authPayload =
      error && typeof error === 'object' && 'error' in error
        ? (error as { error?: { data?: unknown } }).error?.data
        : (error as { data?: unknown })?.data;
    invalidateCheckoutTags(dispatch, authPayload);
  }
};

const extendedApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    makePurchase: builder.mutation<
      PurchaseResponse | RequiresActionResponse,
      PurchaseRequest
    >({
      query: (body) => ({
        url: '/transaction/sale',
        body,
        method: 'POST',
      }),
      invalidatesTags: ['Cart'],
      onQueryStarted: checkoutAuthOnQueryStarted,
    }),
    finalizePurchase: builder.mutation<
      PurchaseResponse | RequiresActionResponse,
      FinalizePurchaseRequest
    >({
      query: (body) => ({
        url: '/transaction/sale/finalize',
        body,
        method: 'POST',
      }),
      invalidatesTags: ['Cart'],
      onQueryStarted: checkoutAuthOnQueryStarted,
    }),
    getConfig: builder.query<GetConfigResponse, GetConfigRequest>({
      query: () => '/config',
      extraOptions: {
        responseSchema: GetConfigResponseSchema,
      },
    }),
    getPrice: builder.query<GetPriceResponse, GetPriceRequest>({
      query: (params) => ({
        url: '/price',
        params,
        method: 'GET',
      }),
      extraOptions: {
        responseSchema: PricesSchema,
      },
    }),
    getCartPrice: builder.mutation({
      query: (body) => ({
        url: '/transaction/cart-price',
        method: 'POST',
        body,
      }),
    }),

    applyCartDiscount: builder.mutation({
      query: (body) => ({
        url: '/price/apply-discount',
        method: 'POST',
        body,
      }),
    }),

    validateDiscount: builder.mutation({
      query: (body: { discountCode: string }) => ({
        url: '/discount/validate',
        method: 'POST',
        body,
      }),
    }),

    createPaypalCheckout: builder.mutation<
      PaypalCheckoutResponse,
      PaypalCheckoutRequest
    >({
      query: (body) => ({
        url: '/paypal/checkout',
        method: 'POST',
        body,
      }),
    }),

    createPaypalCapture: builder.mutation<
      PaypalCaptureResponse,
      PaypalCaptureRequest
    >({
      query: (body) => ({
        url: '/paypal/capture',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Cart'],
      onQueryStarted: checkoutAuthOnQueryStarted,
    }),

    cancelPaypalCheckout: builder.mutation<
      { message: string; cart?: Record<string, unknown> },
      { cartId?: string }
    >({
      query: (body) => ({
        url: '/paypal/cancel',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Cart'],
    }),
  }),
});

export const {
  useMakePurchaseMutation,
  useFinalizePurchaseMutation,
  useGetConfigQuery,
  useGetPriceQuery,
  useGetCartPriceMutation,
  useApplyCartDiscountMutation,
  useValidateDiscountMutation,
  useCreatePaypalCheckoutMutation,
  useCreatePaypalCaptureMutation,
  useCancelPaypalCheckoutMutation,
} = extendedApi;
